<template>
  <!-- Gate de contraseña -->
  <div
    v-if="!unlocked"
    key="lockState"
    class="m-edition-billing-academy-billing academy-billing-lock"
  >
    <div class="academy-billing-lock-card">
      <span class="academy-billing-lock-icon">
        <i class="fas fa-lock"></i>
      </span>
      <h3>{{ $t('mEditionBillingAcademyBilling.lock.title') }}</h3>
      <p>{{ $t('mEditionBillingAcademyBilling.lock.description') }}</p>
      <form
        autocomplete="off"
        name="academy-billing-lock-form"
        @submit.prevent="unlockPanel"
      >
        <AtomInputPrimary
          v-model="passwordInput"
          name="academy-billing-password"
          :placeholder="$t('mEditionBillingAcademyBilling.lock.placeholder')"
          type="password"
          wrapperClass="a-input-wrapper-column"
        />
        <small
          v-if="passwordError"
          class="text-color-danger"
        >
          {{ $t('mEditionBillingAcademyBilling.lock.error') }}
        </small>
        <AtomButtonPrimary
          :button-primary="buttonUnlock"
        />
      </form>
    </div>
  </div>

  <div
    v-else-if="showLoadingState"
    key="loadingState"
  >
    <AtomLoadingSecondary/>
  </div>

  <div
    v-else
    key="academyBilling"
    class="m-edition-billing-academy-billing"
  >
    <!-- Resumen global por divisa -->
    <template v-if="totales.length">
      <p class="academy-billing-summary-hint">
        {{ $t('mEditionBillingAcademyBilling.summary.title') }}
        <small>{{ $t('mEditionBillingAcademyBilling.summary.hint') }}</small>
      </p>
      <div class="academy-billing-summary">
        <div
          v-for="(total, index) in totales"
          :key="index"
          :class="divisaSeleccionada === total.id_divisa
            ? 'academy-billing-summary-card academy-billing-summary-card-active'
            : 'academy-billing-summary-card'"
          @click="toggleDivisa(total.id_divisa)"
        >
          <span class="academy-billing-summary-currency">
            {{ total.divisa_iso_code }}
            <small>{{ total.divisa_nombre }}</small>
          </span>
          <span class="academy-billing-summary-amount">
            {{ formatMoney(total.total, total.divisa_simbolo) }}
          </span>
          <small>
            {{ total.cantidad_pagos }} {{ $t('mEditionBillingAcademyBilling.summary.payments') }}
            · {{ total.cantidad_academias }} {{ $t('mEditionBillingAcademyBilling.summary.academies') }}
          </small>
          <span
            v-if="divisaSeleccionada === total.id_divisa"
            class="academy-billing-summary-check"
          >
            <i class="fas fa-filter"></i>
          </span>
        </div>
      </div>
    </template>

    <!-- Filtros -->
    <form
      autocomplete="off"
      class="academy-billing-filters"
      name="academy-billing-filters-form"
      @submit.prevent="searchSimpleFilter"
    >
      <AtomInputPrimary
        v-model="simpleFilter.url"
        :disabled="pagination.showLoadingState"
        v-on:keyup.enter="searchSimpleFilter"
        :label="$t('mEditionBillingAcademyBilling.filters.urlLabel')"
        name="url"
        :placeholder="$t('mEditionBillingAcademyBilling.filters.urlPlaceholder')"
        type="text"
        wrapperClass="a-input-wrapper-column academy-billing-filter"
      />

      <AtomInputPrimary
        v-model="simpleFilter.producto"
        :disabled="pagination.showLoadingState"
        v-on:keyup.enter="searchSimpleFilter"
        :label="$t('mEditionBillingAcademyBilling.filters.productLabel')"
        name="producto"
        :placeholder="$t('mEditionBillingAcademyBilling.filters.productPlaceholder')"
        type="text"
        wrapperClass="a-input-wrapper-column academy-billing-filter academy-billing-filter-product"
      />

      <AtomInputPrimary
        v-model="simpleFilter.fecha_desde"
        :disabled="pagination.showLoadingState"
        :label="$t('mEditionBillingAcademyBilling.filters.dateFrom')"
        name="fecha_desde"
        type="date"
        wrapperClass="a-input-wrapper-column academy-billing-filter academy-billing-filter-date"
        @change="searchSimpleFilter"
      />

      <AtomInputPrimary
        v-model="simpleFilter.fecha_hasta"
        :disabled="pagination.showLoadingState"
        :label="$t('mEditionBillingAcademyBilling.filters.dateTo')"
        name="fecha_hasta"
        type="date"
        wrapperClass="a-input-wrapper-column academy-billing-filter academy-billing-filter-date"
        @change="searchSimpleFilter"
      />

      <div class="academy-billing-filter-actions">
        <AtomButtonSecondary
          :button-secondary="buttonSecondary.search"
          :disabled="pagination.showLoadingState"
          @searchListing="searchSimpleFilter"
        />
        <AtomButtonSecondary
          :button-secondary="buttonSecondary.reload"
          :disabled="pagination.showLoadingState"
          @reloadListing="reloadListing"
        />
      </div>
    </form>

    <!-- Listado -->
    <div class="m-table-primary">
      <table class="table-layout-fixed">
        <tr v-show="academias.length">
          <th class="text-align-left" width="20%">
            {{ $t('mEditionBillingAcademyBilling.table.thAcademy') }}
          </th>
          <th class="text-align-center" width="7%">
            {{ $t('mEditionBillingAcademyBilling.table.thState') }}
          </th>
          <th class="text-align-center" width="13%">
            {{ $t('mEditionBillingAcademyBilling.table.thLicense') }}
          </th>
          <th class="text-align-center" width="10%">
            {{ $t('mEditionBillingAcademyBilling.table.thSales') }}
          </th>
          <th class="text-align-center" width="9%">
            {{ $t('mEditionBillingAcademyBilling.table.thFirstPayment') }}
          </th>
          <th class="text-align-center" width="9%">
            {{ $t('mEditionBillingAcademyBilling.table.thLastPayment') }}
          </th>
          <th class="text-align-right" width="14%">
            {{ $t('mEditionBillingAcademyBilling.table.thTotals') }}
          </th>
          <th class="text-align-center" width="9%">
            {{ $t('mEditionBillingAcademyBilling.table.thProducts') }}
          </th>
          <th class="text-align-center" width="9%">
            {{ $t('mEditionBillingAcademyBilling.table.thBuyers') }}
          </th>
        </tr>
        <tr
          v-for="(academia, index) in academias"
          :key="index"
          class="row-data"
        >
          <td class="text-align-left">
            <span class="display-flex-column">
              <span class="ellipsis font-weight-bold">{{ academia.nombre_academia }}</span>
              <small class="ellipsis font-weight-regular">{{ academia.url_academia }}</small>
            </span>
          </td>
          <td class="text-align-center">
            <span
              :class="academia.estado_academia === 'ACTIVA'
                ? 'font-weight-bold text-color-success'
                : 'font-weight-regular text-color-danger'"
            >
              {{ academia.estado_academia === 'ACTIVA'
                ? $t('mEditionBillingAcademyBilling.labels.academyStateActive')
                : $t('mEditionBillingAcademyBilling.labels.academyStateHidden') }}
            </span>
          </td>
          <td class="text-align-center">
            <span class="display-flex-column">
              <span
                :class="licenseClass(academia)"
              >
                {{ licenseText(academia) }}
              </span>
              <small
                v-if="academia.licencia && academia.licencia.producto_nombre"
                class="font-weight-regular"
              >
                {{ academia.licencia.producto_nombre }}
                ({{ academia.licencia.licencia_tipo === 'PAGA'
                  ? $t('mEditionBillingAcademyBilling.labels.paidLicense')
                  : $t('mEditionBillingAcademyBilling.labels.freeLicense') }})
              </small>
            </span>
          </td>
          <td class="text-align-center text-white-space-nowrap">
            <span class="display-flex-column">
              <span>{{ academia.cantidad_ventas }} {{ $t('mEditionBillingAcademyBilling.labels.sales') }}</span>
              <small class="font-weight-regular">{{ academia.cantidad_pagos }} {{ $t('mEditionBillingAcademyBilling.labels.payments') }}</small>
            </span>
          </td>
          <td class="text-align-center text-white-space-nowrap">
            {{ academia.fecha_primer_pago
              ? $moment.parseZone(academia.fecha_primer_pago).format('D MMM YYYY')
              : $t('mEditionBillingAcademyBilling.labels.never') }}
          </td>
          <td class="text-align-center text-white-space-nowrap">
            {{ academia.fecha_ultimo_pago
              ? $moment.parseZone(academia.fecha_ultimo_pago).format('D MMM YYYY')
              : $t('mEditionBillingAcademyBilling.labels.never') }}
          </td>
          <td class="text-align-right">
            <span class="display-flex-column">
              <span
                v-for="(total, tIndex) in academia.totales_por_divisa"
                :key="tIndex"
                class="academy-billing-total-chip"
              >
                <strong>{{ total.divisa_iso_code }}</strong>
                {{ formatMoney(total.total, total.divisa_simbolo) }}
              </span>
            </span>
          </td>
          <td class="text-align-center">
            <button
              v-if="academia.productos && academia.productos.length"
              class="a-button-secondary a-button-billing-secondary academy-billing-products-button"
              type="button"
              @click="openProductosModal(academia)"
            >
              <i class="fas fa-box-open margin-right-xsmall"></i>
              {{ academia.productos.length }}
            </button>
            <span v-else>{{ $t('mEditionBillingAcademyBilling.labels.never') }}</span>
          </td>
          <td class="text-align-center">
            <button
              class="a-button-secondary a-button-billing-secondary academy-billing-products-button a-tooltip-primary a-tooltip-bottom"
              :data-tooltip-text="$t('mEditionBillingAcademyBilling.tooltip.buyers')"
              type="button"
              @click="openCompradoresModal(academia)"
            >
              <i class="fas fa-trophy"></i>
            </button>
          </td>
        </tr>
      </table>
      <p
        v-show="!academias.length"
        class="text-align-center"
      >
        {{ $t('mEditionBillingAcademyBilling.labels.noData') }}
      </p>
      <MoleculePaginationPrimary
        :pagination="pagination"
        @setPaginationCurrentPage="setPaginationCurrentPage"
      />
    </div>
  </div>
</template>

<script>
import configApp from '@/config'

const UNLOCK_STORAGE_KEY = 'vidroop-academy-billing-unlocked'

export default {
  name: 'MoleculeEditionBillingAcademyBilling',

  components: {
    MoleculePaginationPrimary: () => import('@/components/molecules/MoleculePaginationPrimary')
  },

  data () {
    return {
      academias: [],

      buttonSecondary: {
        reload: {
          class: 'a-button-billing-secondary a-button-side a-tooltip-primary a-tooltip-bottom',
          component: '',
          icon: 'fas fa-sync-alt',
          method: 'reloadListing',
          text: '',
          tooltip: this.$t('mEditionBillingAcademyBilling.tooltip.reload'),
          type: 'button'
        },

        search: {
          class: 'a-button-billing-secondary a-button-side a-tooltip-primary a-tooltip-bottom',
          component: '',
          icon: 'fas fa-search',
          method: 'searchListing',
          text: '',
          tooltip: this.$t('mEditionBillingAcademyBilling.tooltip.search'),
          type: 'button'
        }
      },

      buttonUnlock: {
        action: this.$t('mEditionBillingAcademyBilling.lock.button'),
        behavior: '',
        class: 'a-button-billing-primary',
        sending: false,
        type: 'submit'
      },

      divisaSeleccionada: '',

      filter: {
        divisa: '',
        fecha_desde: '',
        fecha_hasta: '',
        limite: 10,
        pagina: 1,
        producto: '',
        url: ''
      },

      pagination: {
        currentPage: 1,
        currentQuantity: 0,
        limit: 10,
        showLoadingState: false,
        totalPages: 0,
        totalQuantity: 0
      },

      passwordError: false,

      passwordInput: '',

      showLoadingState: true,

      simpleFilter: {
        fecha_desde: '',
        fecha_hasta: '',
        producto: '',
        url: ''
      },

      totales: [],

      unlocked: false
    }
  },

  created () {
    this.unlocked = sessionStorage.getItem(UNLOCK_STORAGE_KEY) === '1'
    if (this.unlocked) {
      this.readFacturacion()
      this.readTotales()
    }
  },

  methods: {
    formatMoney (value, simbolo) {
      const amount = Number(value || 0).toLocaleString('es-AR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return `${simbolo || ''} ${amount}`
    },

    licenseClass (academia) {
      if (academia.licencia_estado === 'ACTIVA') {
        return 'font-weight-bold text-color-success'
      }
      if (academia.licencia_estado === 'INACTIVA') {
        return 'font-weight-bold text-color-danger'
      }
      return 'font-weight-regular text-color-black-55'
    },

    licenseText (academia) {
      if (academia.licencia_estado === 'ACTIVA') {
        return this.$t('mEditionBillingAcademyBilling.labels.licenseActive')
      }
      if (academia.licencia_estado === 'INACTIVA') {
        return this.$t('mEditionBillingAcademyBilling.labels.licenseInactive')
      }
      return this.$t('mEditionBillingAcademyBilling.labels.noLicense')
    },

    openCompradoresModal (academia) {
      this.$modal.showModal('MoleculeEditionBillingAcademyBillingModalBuyers', {
        divisas: academia.totales_por_divisa || [],
        filtros: {
          fecha_desde: this.filter.fecha_desde,
          fecha_hasta: this.filter.fecha_hasta,
          producto: this.filter.producto
        },
        nombreAcademia: academia.nombre_academia,
        uuidAcademia: academia.id_academia
      })
    },

    openProductosModal (academia) {
      this.$modal.showModal('MoleculeEditionBillingAcademyBillingModalProducts', {
        nombreAcademia: academia.nombre_academia,
        productos: academia.productos,
        onFilter: (uuidProducto) => {
          this.simpleFilter.producto = uuidProducto
          this.searchSimpleFilter()
        }
      })
    },

    readFacturacion () {
      this.pagination.showLoadingState = true
      this.$services.read('academiaIa', 'readFacturacionAcademias', {}, [], this.buildFilter(true))
        .then(response => {
          this.academias = response.data.academias
          this.pagination.currentQuantity = this.academias.length
          this.pagination.totalPages = response.data.pages
          this.pagination.totalQuantity = response.data.total
          this.pagination.showLoadingState = false
          this.showLoadingState = false
        })
        .catch(error => {
          console.log(error)
          this.academias = []
          this.pagination.currentQuantity = 0
          this.pagination.totalPages = 0
          this.pagination.totalQuantity = 0
          this.pagination.showLoadingState = false
          this.showLoadingState = false
          this.$alerts.pushAlert('mEditionBillingAcademyBilling', 'readFacturacion', 'error', 'a-alert-danger')
        })
    },

    readTotales () {
      // Los totales son globales: no se filtran por divisa para que las
      // tarjetas sigan visibles y se pueda alternar entre monedas
      this.$services.read('academiaIa', 'readFacturacionAcademiasTotales', {}, [], this.buildFilter(false))
        .then(response => {
          this.totales = response.data.totales
        })
        .catch(error => {
          console.log(error)
          this.totales = []
          this.$alerts.pushAlert('mEditionBillingAcademyBilling', 'readTotales', 'error', 'a-alert-danger')
        })
    },

    buildFilter (incluirPaginadoYDivisa) {
      const filtros = {
        fecha_desde: this.filter.fecha_desde,
        fecha_hasta: this.filter.fecha_hasta,
        producto: this.filter.producto,
        url: this.filter.url
      }

      if (incluirPaginadoYDivisa) {
        filtros.limite = this.filter.limite
        filtros.pagina = this.filter.pagina
        if (this.divisaSeleccionada) {
          filtros.divisa = [this.divisaSeleccionada]
        }
      }

      return filtros
    },

    reloadListing () {
      this.divisaSeleccionada = ''
      this.simpleFilter = {
        fecha_desde: '',
        fecha_hasta: '',
        producto: '',
        url: ''
      }
      this.searchSimpleFilter()
    },

    searchSimpleFilter () {
      this.filter.url = this.simpleFilter.url
      this.filter.producto = (this.simpleFilter.producto || '').trim()
      this.filter.fecha_desde = this.simpleFilter.fecha_desde
      this.filter.fecha_hasta = this.simpleFilter.fecha_hasta
      this.filter.pagina = 1
      this.pagination.currentPage = 1
      this.readFacturacion()
      this.readTotales()
    },

    setPaginationCurrentPage (page) {
      this.pagination.currentPage = page
      this.filter.pagina = page
      this.readFacturacion()
    },

    toggleDivisa (idDivisa) {
      this.divisaSeleccionada = this.divisaSeleccionada === idDivisa ? '' : idDivisa
      this.filter.pagina = 1
      this.pagination.currentPage = 1
      this.readFacturacion()
    },

    unlockPanel () {
      if (this.passwordInput === configApp.academyBillingPassword) {
        this.passwordError = false
        this.passwordInput = ''
        this.unlocked = true
        sessionStorage.setItem(UNLOCK_STORAGE_KEY, '1')
        this.readFacturacion()
        this.readTotales()
      } else {
        this.passwordError = true
      }
    }
  }
}
</script>
